import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import _cx from 'classnames/bind'
import _ from 'lodash'
import { Table, Icon, Text, Button, Card } from '@adc/ui-adc'
import MockDataImg from '../../images/mock-data.webp'
import MobileMockDataImg from '../../images/mobile-mock-data.webp'
import { useModalFeatures } from '../../hooks/useModalFeatures'
import cls from './index.scss'
const cx = _cx.bind(cls)

const getFieldValue = (comp, fieldInfo = {}) => {
  const val =
    _.get(comp, fieldInfo.fieldPath) || (fieldInfo.hideIfNull ? null : '-')
  return _.isFunction(fieldInfo.format) ? fieldInfo.format(val) : val
}

export const buildRow = ({
  comp,
  key,
  active = false,
  onMouseEnter = _.noop,
  onMouseLeave = _.noop,
  fields,
  elmId,
}) => (
  <Table.Row
    key={key}
    active={active}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {fields.map((field, i) => (
      <Table.Cell
        key={i}
        data-elm-id={`${elmId}_${field.elmId || _.snakeCase(field.label)}`}
      >
        {getFieldValue(comp, field)}
      </Table.Cell>
    ))}
  </Table.Row>
)

buildRow.propTypes = {
  comp: PropTypes.number.isRequired,
  key: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  elmId: PropTypes.string.isRequired,
}

export const CompCard = ({ comp, fields = [] }) => {
  const fieldsCopy = fields.slice(0)
  const [address, price] = fieldsCopy.splice(0, 2)
  const rows = _.chunk(fieldsCopy, 2)
  return (
    <div className={cx('comp-card')}>
      <Card>
        <Card.Content>
          <Card.Header>
            <Text size='huge' weight='heavy'>
              {getFieldValue(comp, price)}
            </Text>
          </Card.Header>
          <Card.Description>
            <Text size='large'>{getFieldValue(comp, address)}</Text>
          </Card.Description>
          <table className={cx('u-mt-3')}>
            <tbody>
              {rows.map((row, i) => {
                const [first, second] = row
                return (
                  <tr key={i} className={cx('mobile-row')}>
                    <td>
                      <Text color='gray'>{first.label}</Text>
                    </td>
                    <td>
                      <Text weight='heavy'>{getFieldValue(comp, first)}</Text>
                    </td>
                    {second && (
                      <>
                        <td>
                          <Text color='gray'>{second.label}</Text>
                        </td>
                        <td>
                          <Text weight='heavy'>
                            {getFieldValue(comp, second)}
                          </Text>
                        </td>
                      </>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card.Content>
      </Card>
    </div>
  )
}

CompCard.propTypes = {
  comp: PropTypes.object.isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export const DataNotAvailable = () => (
  <div className={cx('no-data-available')}>
    <div className={cx('u-mb-2')}>
      <Icon
        name='exclamation-triangle'
        iconStyle='regular'
        color='caution'
        size='big'
      />
    </div>
    <Text.Header compact level='2' weight='normal' className={cx('u-mb-1')}>
      Currently, data is not available.
    </Text.Header>
    <Text.Header compact level='2' weight='normal'>
      Come back later to review details when they're available.
    </Text.Header>
  </div>
)

const Protected = ({ isLoggedIn, children }) => {
  const { toggleAuthModal } = useModalFeatures()
  return isLoggedIn ? (
    children
  ) : (
    <>
      <div className={cx('protected')}>
        <img className={cx('mock-data-img')} alt='map data' src={MockDataImg} />
        <img
          className={cx('mobile-mock-data-img')}
          alt='mobile map data'
          src={MobileMockDataImg}
        />
      </div>
      <div className={cx('login-container')}>
        <div className={cx('login-modal')}>
          <div className='u-mb-2'>
            <Icon
              name='lock'
              size='huge'
              iconStyle='regular'
              color='grayDark'
            />
          </div>
          <Text.Header level='2'>Unlock local market data</Text.Header>
          <Button
            role='primary'
            elmId='market_analysis_login_cta'
            onClick={() => toggleAuthModal('login', 'compsales')}
          >
            Log In
          </Button>
          <Text className={cx('u-mt-2')}>
            Don't have an account yet?{' '}
            <a
              onClick={() => toggleAuthModal('signup', 'compsales')}
              data-elm-id='market_analysis_registration_cta'
            >
              Sign up now
            </a>
          </Text>
        </div>
      </div>
    </>
  )
}
Protected.propTypes = {
  isLoggedIn: PropTypes.bool,
  children: PropTypes.node,
}

const mapStateToProps = ({ user }) => ({
  isLoggedIn: user.logged_in,
})

const ProtectedView = connect(mapStateToProps)(Protected)

export const privateView = Component => props =>
  (
    <ProtectedView>
      <Component {...props} />
    </ProtectedView>
  )
